import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Clock, Gauge, Trash2, BellRing } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useDeleteSchedule, useUpdateSchedule } from '@/features/service/hooks'
import type { ServiceSchedule } from '@/types/service'

/** Days until next_due_at (negative = overdue). */
export function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Math.round(ms / 86_400_000)
}

export function DueBadge({ schedule }: { schedule: ServiceSchedule }) {
  const days = daysUntil(schedule.next_due_at)
  if (!schedule.active) return <Badge variant="outline">Paused</Badge>
  if (days < 0) return <Badge variant="destructive">Overdue {Math.abs(days)}d</Badge>
  if (days <= schedule.reminder_lead_days)
    return <Badge className="bg-amber-500 hover:bg-amber-500">Due in {days}d</Badge>
  return <Badge variant="secondary">In {days}d</Badge>
}

interface ScheduleListProps {
  schedules: ServiceSchedule[]
  isLoading?: boolean
  /** Show the boat column (hidden on per-boat views). */
  showBoat?: boolean
  boatNames?: Record<string, string>
  emptyMessage?: string
}

export function ScheduleList({
  schedules,
  isLoading,
  showBoat = false,
  boatNames = {},
  emptyMessage = 'No recurring schedules yet.',
}: ScheduleListProps) {
  const deleteMutation = useDeleteSchedule()
  const updateMutation = useUpdateSchedule()
  const [deleting, setDeleting] = useState<ServiceSchedule | null>(null)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (schedules.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Task</TableHead>
            {showBoat && <TableHead>Boat</TableHead>}
            <TableHead>Interval</TableHead>
            <TableHead>Next due</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {schedules.map((s) => (
            <TableRow key={s.id} className={!s.active ? 'opacity-60' : undefined}>
              <TableCell>
                <div className="font-medium">{s.title}</div>
                <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {s.auto_create_work_order && (
                    <span className="inline-flex items-center gap-1">
                      <BellRing className="h-3 w-3" /> auto work order
                    </span>
                  )}
                  <span>· reminds {s.reminder_lead_days}d ahead</span>
                </div>
              </TableCell>
              {showBoat && (
                <TableCell>
                  <Link
                    to="/inventory/$boatId"
                    params={{ boatId: s.boat_id }}
                    className="hover:underline"
                  >
                    {boatNames[s.boat_id] ?? 'View boat'}
                  </Link>
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap text-sm">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" /> {s.interval_days}d
                </span>
                {s.interval_hours != null && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    <Gauge className="h-3.5 w-3.5 text-muted-foreground" /> {s.interval_hours}h
                  </span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="flex flex-col gap-1">
                  <DueBadge schedule={s} />
                  <span className="text-xs text-muted-foreground">
                    {new Date(s.next_due_at).toLocaleDateString()}
                    {s.next_due_hours != null && ` · ${s.next_due_hours} hrs`}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {s.priority}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={updateMutation.isPending}
                    onClick={() => updateMutation.mutate({ id: s.id, body: { active: !s.active } })}
                  >
                    {s.active ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleting(s)}
                    aria-label={`Delete ${s.title}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Delete confirmation — deleting stops the reminders and any future
          auto-created work orders. */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete schedule “{deleting?.title}”?</DialogTitle>
            <DialogDescription>
              The recurring schedule stops generating reminders and work orders. Past work orders
              are kept. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleting && deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
              }
            >
              Delete Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
